import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table'
import {
  notUndefined,
  useVirtualizer,
  VirtualItem,
} from '@tanstack/react-virtual'
import Checkbox from 'components/Checkbox/checkbox'
import { DataTableSelectAllDropdown } from 'components/DataTable/DataTableSelectAllDropdown/DataTableSelectAllDropdown'
import {
  ColumnModal,
  getColumnSelectorRecipientId,
} from 'components/Modals/ColumnModal/ColumnModal'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import React, {
  Fragment,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useAuth } from 'context/authentication/useAuth'
import {
  applyColumnMinWidths,
  resolveColumnDefId,
  resolveColumnMinWidth,
  useColumnWidthPersistence,
  useMeasuredFloor,
} from '../columnSizing'
import { Spinner } from 'react-bootstrap'
import { createPortal } from 'react-dom'
import { FaChevronDown, FaChevronRight } from 'react-icons/fa'
import SimpleBar from 'simplebar-react'
import 'simplebar-react/dist/simplebar.min.css'
import {
  useColumnOrder,
  useColumnVisibility,
} from 'stores/ColumnsStore/ColumnsStore'
import { useColumnStoreContext } from 'stores/ColumnsStore/useColumnsStoreContext'
import styled from 'styled-components/macro'
import { ConditionalWrapper } from '../../ConditionalWrapper'
import { cn } from '../../UI/cn'
import { useDataTableContext } from '../DataTableContext'
import DataTablePagination from '../DataTablePagination/DataTablePagination'
import {
  DataTableColumnSort,
  dataTableSortingStoreRepo,
} from '../DataTableSorting/DataTableSortingStore'
import { DataTableSpreadsheetCell } from '../spreadsheet/DataTableSpreadsheetCell'
import { DataTableSpreadsheetProvider } from '../spreadsheet/DataTableSpreadsheetContext'
import type { SpreadsheetEditableMeta } from '../spreadsheet/spreadsheetTypes'
import { SortAscIcon, SortDescIcon } from '../styles'
import { ITablePaginationOptions } from '../types'
import { useToolbarDensity } from 'components/Tables/CommonTableCells/ToolbarDensityContext'
import * as S from './styles'

// px reserved for the header affordance (info tooltip / sort icon) + its gap
// when deciding whether the full title still fits alongside it.
const HEADER_AFFORDANCE_RESERVE_PX = 28

// Toggle data-cramped on a header-content node: the affordance is shown only
// when the column is wide enough for the full (natural, single-line) title plus
// the reserve. Idempotent — the title's scrollWidth and the node's clientWidth
// don't depend on whether the affordance is currently hidden, so this can't
// oscillate.
function updateHeaderCramp(node: HTMLElement) {
  const title = node.querySelector<HTMLElement>('[data-header-title]')
  if (!title) return
  const fits =
    node.clientWidth >= title.scrollWidth + HEADER_AFFORDANCE_RESERVE_PX
  const next = fits ? 'false' : 'true'
  if (node.dataset.cramped !== next) node.dataset.cramped = next
}

export interface DataTableProps<T> {
  columns: ColumnDef<T, any>[]
  data: T[]
  extraRowAccessor?: keyof T
  expandColAccessor?: keyof T | string
  rowAttributes?: (
    row: Row<T>
  ) => { [key: string]: string } & HTMLAttributes<any>
  children?: ReactNode
  sortableFields?: Array<string> | 'all'
  defaultSort?: DataTableColumnSort[]
  onTableBottomReached?: () => void
  virtualizeRows?: boolean
  enableRowSelection?: boolean
  localSort?: boolean
  isPaginationEnabled?: boolean
  paginationOptions?: ITablePaginationOptions
  stickyFirstColumn?: boolean
  stickyLastColumn?: boolean
  preventLastColumnSticky?: boolean
  /** Opt-in: allow users to drag-resize columns. Widths persist per user+table
   *  in localStorage and are floored by each column's content-type minimum
   *  (see ../columnSizing). Default off so existing tables are unchanged. */
  enableColumnResizing?: boolean
  style?: React.CSSProperties
  disabledRowsIdx?: number[]
  autoHideScrollbar?: boolean
  height?: number
  /** Content row height in px (header row is separate). Defaults to 56. */
  rowHeight?: number
  /** Header row height in px. Defaults to 44. */
  headerHeight?: number
  tableKey: string
  footerControls?: React.JSX.Element
  footerLeftControls?: React.JSX.Element
  selectAllText?: string
  /** Optional content rendered inside the Select column header, after the
      select-all dropdown. Used on DS2026 surfaces to host the bulk Actions
      button next to the "Selected" label. */
  selectHeaderActions?: React.ReactNode
  loading?: boolean
  tableHeader?: React.JSX.Element
  emptyTableText?: {
    title: string
    subtitle?: string
  }
  /** Render the empty state as an in-flow card with this minimum height instead of the default absolute overlay. */
  emptyStateMinHeight?: string
  extraCellColSpan?: number
  onRowDoubleClick?: (row: Row<T>) => void
  onRowClick?: (row: Row<T>) => void
  getRowId?: TableOptions<T>['getRowId']
}

export default function DataTable<T extends object>(props: DataTableProps<T>) {
  // Footer density follows the toolbar density: a compact (sm/xs) toolbar gets
  // the 52px footer reserve + compact pagination; tables with no
  // ToolbarDensityProvider keep the legacy 72px reserve + 40px pagination.
  const toolbarDensity = useToolbarDensity()
  const isCompactFooter = toolbarDensity !== 'md'
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const columnVisibility = useColumnVisibility()
  const columnOrder = useColumnOrder()
  const initializeTableColumns = useColumnStoreContext(
    (state) => state.initializeTableColumns
  )
  const { sorting, setSorting, handleSort } =
    dataTableSortingStoreRepo.getStore(props.tableKey)()

  const {
    state: {
      expanded,
      rowSelection,
      totalSelectedRows,
      totalRowsInBackend,
      isAllRowsSelected,
    },
    methods: {
      setExpanded,
      setRowSelection,
      setIsAllRowsSelected,
      setTotalRowsInPage,
    },
  } = useDataTableContext()

  const { user } = useAuth()
  const rowHeight = props.rowHeight ?? 56
  const headerHeight = props.headerHeight ?? 44

  // The column displayed first hosts the bulk-select control; it gets the
  // measured actions-button floor. Prefer the live display order, fall back to
  // the isFirstColumn flag / first definition.
  const firstColumnId = useMemo(() => {
    const excluded = new Set(
      [props.extraRowAccessor, props.expandColAccessor]
        .filter(Boolean)
        .map(String)
    )
    const fromOrder = columnOrder?.find((id) => !excluded.has(id))
    if (fromOrder) return fromOrder
    const flagged = props.columns.find(
      (c) => (c.meta as { isFirstColumn?: boolean } | undefined)?.isFirstColumn
    )
    return resolveColumnDefId(flagged ?? props.columns[0])
  }, [
    columnOrder,
    props.columns,
    props.extraRowAccessor,
    props.expandColAccessor,
  ])

  // Live natural width of the first-column bulk-select + Actions control, used
  // as that column's minimum so it can never be clipped. +24 ≈ cell padding.
  const [measuredSelectFloor, selectFloorRef] = useMeasuredFloor(24)

  // One ResizeObserver shared across all header cells: when a column is dragged
  // too narrow to fit its title + affordance, the affordance is hidden (see
  // updateHeaderCramp + the [data-cramped] CSS rule). A single observer avoids
  // per-header hooks (the header list is a .map).
  const headerCrampObserverRef = useRef<ResizeObserver | null>(null)
  // Track which header nodes we're observing so stale ones can be unobserved.
  // The callback ref fires with `null` on unmount but doesn't tell us which
  // node left, so instead of a useEffect (forbidden — see client/CLAUDE.md) we
  // prune any observed node that's no longer in the DOM on every ref call
  // (mount AND unmount both trigger one). This keeps the single observer from
  // accumulating stale observations across column reorder / visibility toggles.
  const observedHeadersRef = useRef<Set<HTMLElement>>(new Set())
  const observeHeaderContent = React.useCallback((node: HTMLElement | null) => {
    if (typeof ResizeObserver === 'undefined') return

    const observer = headerCrampObserverRef.current
    if (observer) {
      observedHeadersRef.current.forEach((observed) => {
        if (!observed.isConnected) {
          observer.unobserve(observed)
          observedHeadersRef.current.delete(observed)
        }
      })
    }

    if (!node) return

    if (!headerCrampObserverRef.current) {
      headerCrampObserverRef.current = new ResizeObserver((entries) => {
        entries.forEach((entry) =>
          updateHeaderCramp(entry.target as HTMLElement)
        )
      })
    }
    headerCrampObserverRef.current.observe(node)
    observedHeadersRef.current.add(node)
    updateHeaderCramp(node)
  }, [])

  const [columnSizing, onColumnSizingChange] = useColumnWidthPersistence({
    tableKey: props.tableKey,
    userId: user?.id,
    enabled: !!props.enableColumnResizing,
  })

  // Inject content-type minimum widths (and the measured first-column floor)
  // onto the column defs so TanStack's getSize() clamps to them.
  const sizedColumns = useMemo(() => {
    if (!props.enableColumnResizing) return props.columns
    return applyColumnMinWidths(props.columns, {
      firstColumnId,
      measuredSelectFloor,
      rowSelectionEnabled: props.enableRowSelection,
    })
  }, [
    props.columns,
    props.enableColumnResizing,
    props.enableRowSelection,
    firstColumnId,
    measuredSelectFloor,
  ])

  const tableOptions = {
    data: props.data,
    columns: sizedColumns,
    state: {
      columnVisibility: columnVisibility,
      columnOrder: columnOrder,
      rowSelection: rowSelection,
      ...(props.enableColumnResizing ? { columnSizing } : {}),
    },
    enableRowSelection: props.enableRowSelection,
    enableColumnResizing: props.enableColumnResizing ?? false,
    columnResizeMode: 'onChange' as const,
    onColumnSizingChange: props.enableColumnResizing
      ? onColumnSizingChange
      : undefined,
    onSortingChange: (a: any) => {
      setSorting(a())
    },
    onRowSelectionChange: setRowSelection,
    getRowId: props.getRowId,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  } as TableOptions<T>

  if (props.localSort && tableOptions.state) {
    tableOptions.state.sorting = sorting as SortingState
  }

  const table = useReactTable(tableOptions)
  const allColumns = table.getAllColumns()

  useEffect(() => {
    initializeTableColumns(allColumns as any)
  }, [allColumns, initializeTableColumns])

  useEffect(() => {
    // Sets default sorting state — intentionally mount-only
    if (props.defaultSort)
      setSorting([
        {
          id: props.defaultSort[0].id,
          desc: props.defaultSort[0].desc,
        },
      ])
    handleScroll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Calling browser apis directly prevents stutters from react rerenders when using useState
  function handleScroll(event?: React.UIEvent<HTMLDivElement, UIEvent>) {
    const scrollElement = event?.currentTarget ?? tableContainerRef.current
    if (!scrollElement) return

    const {
      scrollHeight,
      scrollTop,
      clientHeight,
      scrollLeft,
      scrollWidth,
      clientWidth,
    } = scrollElement

    // Show left shadow when scrolled away from the left edge
    if (scrollLeft > 0) {
      document
        .querySelectorAll('.shadowable-table-cell')
        .forEach((el) => el?.classList.add('shadow-table-cell'))
    } else {
      document
        .querySelectorAll('.shadowable-table-cell')
        .forEach((el) => el?.classList.remove('shadow-table-cell'))
    }

    // Show right shadow only when there's horizontal overflow AND not scrolled to the right edge
    const hasHorizontalOverflow = scrollWidth > clientWidth
    const isScrolledToRight =
      Math.abs(scrollLeft - (scrollWidth - clientWidth)) < 1
    if (hasHorizontalOverflow && !isScrolledToRight) {
      document
        .querySelectorAll('.shadowable-table-cell-right')
        .forEach((el) => el?.classList.add('shadow-table-cell-right'))
    } else {
      document
        .querySelectorAll('.shadowable-table-cell-right')
        .forEach((el) => el?.classList.remove('shadow-table-cell-right'))
    }

    // called once the user has scrolled within 300px of the bottom of the table
    if (scrollHeight - scrollTop - clientHeight < 300) {
      if (props.onTableBottomReached) props.onTableBottomReached()
    }
  }

  const rowModel = useMemo(
    () => table.getRowModel(),
    [props.tableKey, props.data]
  )

  const virtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rowModel.rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => rowHeight,
    overscan: 5,
    scrollMargin: 0,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
  })

  const virtualRows = virtualizer.getVirtualItems()

  const [before, after] = useMemo(() => {
    return virtualRows.length > 0
      ? [
          notUndefined(virtualRows[0]).start - virtualizer.options.scrollMargin,
          virtualizer.getTotalSize() -
            notUndefined(virtualRows[virtualRows.length - 1]).end,
        ]
      : [0, 0]
  }, [
    virtualRows,
    virtualizer.options.scrollMargin,
    virtualizer.getTotalSize(),
  ])

  const orderedRows = useMemo(
    () => rowModel.rows.map((row) => row.original),
    [rowModel.rows]
  )
  const orderedRowIds = useMemo(
    () => rowModel.rows.map((row) => row.id),
    [rowModel.rows]
  )

  const rowIdsByOriginal = useMemo(() => {
    return new Map(rowModel.rows.map((row) => [row.original, row.id] as const))
  }, [rowModel.rows])
  const visibleSpreadsheetColumnIds = useMemo(() => {
    if (props.virtualizeRows) return []

    return (
      rowModel.rows[0]
        ?.getVisibleCells()
        .flatMap((cell) =>
          (cell.column.columnDef.meta as any)?.spreadsheetEditable
            ? [cell.column.id]
            : []
        ) ?? []
    )
  }, [props.virtualizeRows, rowModel.rows, columnOrder, columnVisibility])
  // Use ASCII control chars (U+001F unit separator inside groups, U+001E
  // record separator between groups) as delimiters so they cannot collide
  // with any printable character a row or column id might contain.
  const spreadsheetStateKey = useMemo(
    () =>
      [
        orderedRowIds.join('\u001f'),
        visibleSpreadsheetColumnIds.join('\u001f'),
      ].join('\u001e'),
    [orderedRowIds, visibleSpreadsheetColumnIds]
  )

  const getSortIcon = (columnId: string) => {
    if (!sorting || sorting.length < 1) return null
    const sort = sorting?.[0]?.id === columnId
    if (sort) {
      return (
        <div className={'ml-3'}>
          {sorting[0]?.desc ? <SortDescIcon /> : <SortAscIcon />}
        </div>
      )
    }
    return null
  }

  useEffect(() => {
    setTotalRowsInPage(table.getRowCount())
  }, [tableOptions, table])

  const toggleSelectAll = () => {
    const selected = !isAllRowsSelected
    setIsAllRowsSelected(selected)
    table.toggleAllRowsSelected(selected)
  }

  return (
    <div className={'flex flex-col w-full relative h-full'}>
      {props.tableHeader}

      <div
        data-table-frame="true"
        data-row-selection={props.enableRowSelection ? 'true' : 'false'}
        style={{
          border: '1px solid #eaecf0',
          borderRadius: 8,
          overflow: 'hidden',
          height: props.height || '100%',
          minHeight: 240,
          position: 'relative',
        }}
      >
        <SimpleBar
          style={{
            height: props.height || '100%',
            ...props.style,
          }}
          autoHide={props.autoHideScrollbar}
          scrollableNodeProps={{
            ref: tableContainerRef,
            onScroll: handleScroll,
          }}
        >
          <DataTableSpreadsheetProvider
            key={spreadsheetStateKey}
            orderedRows={orderedRows}
            getRowId={(row) => rowIdsByOriginal.get(row) ?? ''}
          >
            <S.Table ref={tableRef} role="grid" data-spreadsheet-root="true">
              <S.THead>
                {table.getHeaderGroups().map((headerGroup) => {
                  const filteredHeaderGroup = headerGroup.headers.filter(
                    ({ id }) =>
                      id !== props.extraRowAccessor &&
                      id !== props.expandColAccessor
                  )
                  return (
                    <S.TRHead
                      key={`headergroup_${headerGroup.id}`}
                      style={{ height: headerHeight }}
                    >
                      {filteredHeaderGroup.map((header, headerIndx) => {
                        const sortingProps: Record<string, unknown> = {}

                        const isSortable =
                          (props.sortableFields === 'all' ||
                            props.sortableFields?.includes(header.column.id)) &&
                          header.column.id !== 'select'

                        if (isSortable) {
                          sortingProps['onClick'] = () =>
                            handleSort(header.column.id)
                          sortingProps['style'] = {
                            cursor: 'pointer',
                          }
                        }

                        const tooltip = (header?.column.columnDef?.meta as any)
                          ?.tooltip

                        const isSticky = (header?.column.columnDef?.meta as any)
                          ?.isSticky
                        const isLastColumn = (
                          header?.column.columnDef?.meta as any
                        )?.isLastColumn

                        const stickyStyle: React.CSSProperties = {
                          width: header.getSize(),
                        }
                        if (props.enableColumnResizing) {
                          // Belt-and-suspenders: getSize() already clamps to
                          // minSize, but pin min-width too so the cell can never
                          // collapse below its content-type floor.
                          stickyStyle.minWidth = resolveColumnMinWidth(
                            header.column.columnDef,
                            {
                              isFirstColumn:
                                !!props.enableRowSelection &&
                                header.column.id === firstColumnId,
                              measuredSelectFloor,
                            }
                          )
                          // Note: S.TH is already position: sticky, which
                          // anchors the absolutely-positioned resize handle.
                        }
                        if (isSticky) {
                          stickyStyle.left = 0
                          stickyStyle.zIndex = 30 // Higher than default TABLE_STICKY (20)
                        }
                        if (isLastColumn && !props.preventLastColumnSticky) {
                          stickyStyle.right = 0
                          stickyStyle.zIndex = 30 // Higher than default TABLE_STICKY (20)
                        }

                        const canResize =
                          props.enableColumnResizing &&
                          header.column.getCanResize()

                        return (
                          <S.TH
                            style={stickyStyle}
                            key={header.id}
                            colSpan={header.colSpan}
                            // Marks the actual Actions/trailing column (meta
                            // isLastColumn / columnType 'actions') so the DS2026
                            // header-centering CSS targets it specifically —
                            // not whatever column happens to be positionally
                            // last (e.g. Known GPOs on a table with no Actions).
                            data-actions-header={
                              isLastColumn ||
                              (header?.column.columnDef?.meta as any)
                                ?.columnType === 'actions'
                                ? 'true'
                                : undefined
                            }
                            className={cn({
                              'sticky-table-cell':
                                (headerIndx === 0 && props.stickyFirstColumn) ||
                                isSticky,
                              'sticky-table-cell-right':
                                (props.stickyLastColumn &&
                                  headerIndx ===
                                    filteredHeaderGroup.length - 1) ||
                                (isLastColumn &&
                                  !props.preventLastColumnSticky),
                              'shadowable-table-cell':
                                (headerIndx === 0 && props.stickyFirstColumn) ||
                                isSticky,
                              'shadowable-table-cell-right':
                                (props.stickyLastColumn &&
                                  headerIndx ===
                                    filteredHeaderGroup.length - 1) ||
                                (isLastColumn &&
                                  !props.preventLastColumnSticky),
                            })}
                          >
                            <ConditionalWrapper
                              condition={
                                !!(props.enableRowSelection && headerIndx === 0)
                              }
                              wrapper={(children) => (
                                <S.CheckboxRow>{children}</S.CheckboxRow>
                              )}
                            >
                              <>
                                {props.enableRowSelection &&
                                  headerIndx === 0 && (
                                    <ConditionalWrapper
                                      condition={!!props.enableColumnResizing}
                                      wrapper={(children) => (
                                        // Shrink-wrapped (w-max) so the measured
                                        // scrollWidth is the control's natural
                                        // width, independent of the cell width.
                                        <div
                                          ref={selectFloorRef}
                                          className="inline-flex w-max items-center gap-3"
                                        >
                                          {children}
                                        </div>
                                      )}
                                    >
                                      <>
                                        <DataTableSelectAllDropdown
                                          selectAllText={props.selectAllText}
                                          listCount={totalRowsInBackend}
                                          pageCount={table.getRowCount()}
                                          isAllRowsSelected={
                                            isAllRowsSelected &&
                                            table.getIsAllRowsSelected()
                                          }
                                          totalSelected={
                                            isAllRowsSelected
                                              ? totalSelectedRows
                                              : Object.keys(rowSelection).length
                                          }
                                          onSelectAllInList={() => {
                                            // Select all items in the list
                                            toggleSelectAll()
                                          }}
                                          onSelectAllInPage={() => {
                                            // Select all items in the current page
                                            setIsAllRowsSelected(false)
                                            table.toggleAllRowsSelected(true)
                                          }}
                                          onSelectNone={() => {
                                            // Deselect all items
                                            table.setRowSelection({})
                                            setIsAllRowsSelected(false)
                                            table.toggleAllRowsSelected(false)
                                          }}
                                        />
                                        {props.selectHeaderActions && (
                                          <div className="ml-2">
                                            {props.selectHeaderActions}
                                          </div>
                                        )}
                                      </>
                                    </ConditionalWrapper>
                                  )}
                                {!header.isPlaceholder && (
                                  <div
                                    data-column-header
                                    ref={
                                      props.enableColumnResizing
                                        ? observeHeaderContent
                                        : undefined
                                    }
                                    className={cn(
                                      'flex items-center justify-start',
                                      {
                                        'justify-end': (
                                          header?.column.columnDef?.meta as any
                                        )?.rightAlign,
                                        // Single-line headers when resizing so
                                        // the affordance-fit measurement is
                                        // reliable; affordances hide when cramped.
                                        'min-w-0 overflow-hidden':
                                          props.enableColumnResizing,
                                      }
                                    )}
                                  >
                                    <S.HeadingText
                                      data-header-title
                                      rightAlign={
                                        (header?.column.columnDef?.meta as any)
                                          ?.rightAlign
                                      }
                                      className={cn({
                                        'truncate min-w-0':
                                          props.enableColumnResizing,
                                      })}
                                      {...sortingProps}
                                    >
                                      {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                      )}
                                    </S.HeadingText>
                                    {tooltip ? (
                                      <div
                                        data-header-affordance
                                        className={'ml-2 shrink-0'}
                                      >
                                        <HelpTooltip
                                          title={tooltip?.title}
                                          content={tooltip?.content}
                                          link={{
                                            text: tooltip?.link_text,
                                            href: tooltip?.link,
                                          }}
                                        />
                                      </div>
                                    ) : null}
                                    {/* Sort icon must always stay visible (more
                                        important than the info tooltip), so it
                                        is NOT marked data-header-affordance. It
                                        is shrink-0 and the title truncates to
                                        make room for it. */}
                                    <div
                                      data-sort-icon="true"
                                      className="shrink-0"
                                      {...sortingProps}
                                    >
                                      {getSortIcon(header.column.id)}
                                    </div>
                                  </div>
                                )}
                              </>
                            </ConditionalWrapper>
                            {canResize && (
                              <S.ColumnResizeHandle
                                role="separator"
                                aria-orientation="vertical"
                                aria-label={`Resize column`}
                                data-resizing={
                                  header.column.getIsResizing() || undefined
                                }
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                          </S.TH>
                        )
                      })}
                    </S.TRHead>
                  )
                })}
              </S.THead>
              <S.TBody
                style={
                  props.virtualizeRows
                    ? {
                        height: `${virtualizer.getTotalSize()}px`,
                      }
                    : undefined
                }
              >
                {props.virtualizeRows && before > 0 && (
                  <S.TR
                    style={{
                      height: `${before}px`,
                    }}
                  />
                )}

                {(props.virtualizeRows ? virtualRows : rowModel.rows).map(
                  (_row, rowPosition) => {
                    const row = (
                      props.virtualizeRows ? rowModel.rows[_row.index] : _row
                    ) as Row<T>
                    const spreadsheetRowIndex = props.virtualizeRows
                      ? _row.index
                      : rowPosition
                    const cells = row.getVisibleCells()
                    const isLast = rowModel.rows.length === _row.index + 1

                    const indexOfExtraCell = cells.findIndex(
                      (c) => c.column.id === props.extraRowAccessor
                    )
                    const extraCell =
                      indexOfExtraCell !== -1
                        ? cells.splice(indexOfExtraCell, 1)[0]
                        : null

                    const indexOfExpandCell = props.expandColAccessor
                      ? cells.findIndex((c) =>
                          c.column.id.includes(
                            props.expandColAccessor?.toString() ?? ''
                          )
                        )
                      : -1
                    const expandCell =
                      indexOfExpandCell !== -1 ? cells[indexOfExpandCell] : null

                    const filteredCells = cells.filter((c) => {
                      let isExpand = false
                      if (props.expandColAccessor) {
                        isExpand = c.column.id.includes(
                          props.expandColAccessor.toString()
                        )
                      }
                      return !isExpand
                    })

                    const isExpanded = expandCell
                      ? expanded.includes(expandCell.id)
                      : false
                    const attrs = props.rowAttributes?.(row)

                    const rowProps = {
                      ...attrs,
                      onClick: () => {
                        if (row.getCanExpand()) {
                          const expandSubRowHandler =
                            row.getToggleExpandedHandler()
                          expandSubRowHandler()
                        } else if (expandCell && props.expandColAccessor) {
                          setExpanded((prevExpanded) => {
                            if (prevExpanded.includes(expandCell.id)) {
                              return prevExpanded.filter(
                                (id) => id !== expandCell.id
                              )
                            } else {
                              return [...prevExpanded, expandCell.id]
                            }
                          })
                        }
                        if (props.onRowClick) {
                          props.onRowClick(row)
                        }
                      },
                      style: {
                        cursor:
                          expandCell || props.onRowClick
                            ? 'pointer'
                            : 'default',
                      },
                    }

                    return (
                      <Fragment key={row.id}>
                        <S.TR
                          {...rowProps}
                          expandCell={!!expandCell || row.getCanExpand()}
                          isExpanded={!!extraCell || isExpanded}
                          isSelected={row.getIsSelected()}
                          isDisabled={
                            props.disabledRowsIdx?.includes(row.index) || false
                          }
                          key={
                            props.virtualizeRows
                              ? (_row as VirtualItem).key
                              : (_row as Row<T>).id
                          }
                          data-selected={row.getIsSelected() || undefined}
                          data-index={
                            props.virtualizeRows ? _row.index : undefined
                          }
                          ref={
                            props.virtualizeRows
                              ? virtualizer.measureElement
                              : undefined
                          }
                          style={{
                            height: `${rowHeight}px`,
                          }}
                          onDoubleClick={() => {
                            if (props.onRowDoubleClick) {
                              props.onRowDoubleClick(row)
                            }
                          }}
                        >
                          {filteredCells.map((cell, indx) => {
                            const meta = cell?.column.columnDef?.meta as any
                            const rightAlign = meta?.rightAlign
                            const isSticky = meta?.isSticky
                            const isLastColumn = meta?.isLastColumn
                            const isStickyCell =
                              (indx === 0 && props.stickyFirstColumn) ||
                              isSticky
                            const isRightStickyCell =
                              (props.stickyLastColumn &&
                                indx === filteredCells.length - 1) ||
                              (isLastColumn && !props.preventLastColumnSticky)
                            const spreadsheetEditable = props.virtualizeRows
                              ? undefined
                              : (meta?.spreadsheetEditable as
                                  | SpreadsheetEditableMeta<T>
                                  | undefined)

                            const cellStyle: React.CSSProperties = {
                              height: `${rowHeight}px`,
                            }
                            if (props.enableColumnResizing) {
                              // Lock body cells to the SAME width + floor as the
                              // header so the two tracks can't diverge (a
                              // non-wrapping body value must not make the cell
                              // wider than its header). width === getSize()
                              // already clamps to minSize; minWidth pins it too.
                              const width = cell.column.getSize()
                              cellStyle.width = width
                              cellStyle.maxWidth = width
                              cellStyle.minWidth = resolveColumnMinWidth(
                                cell.column.columnDef,
                                {
                                  isFirstColumn:
                                    !!props.enableRowSelection && indx === 0,
                                  measuredSelectFloor,
                                }
                              )
                            }
                            if (spreadsheetEditable) {
                              cellStyle.padding = 0
                              if (!isStickyCell && !isRightStickyCell) {
                                cellStyle.position = 'relative'
                              }
                            }
                            if (isSticky) {
                              cellStyle.left = 0
                              cellStyle.zIndex = 15 // Higher than default (no z-index on TD)
                            }
                            if (
                              isLastColumn &&
                              !props.preventLastColumnSticky
                            ) {
                              cellStyle.right = 0
                              cellStyle.zIndex = 15 // Higher than default (no z-index on TD)
                            }

                            return (
                              <S.TD
                                className={cn({
                                  'sticky-table-cell': isStickyCell,
                                  'shadowable-table-cell': isStickyCell,
                                  'sticky-table-cell-right': isRightStickyCell,
                                  'shadowable-table-cell-right':
                                    isRightStickyCell,
                                  'text-right': rightAlign,
                                })}
                                key={cell.id}
                                style={cellStyle}
                              >
                                <ConditionalWrapper
                                  condition={
                                    !!(props.enableRowSelection && indx === 0)
                                  }
                                  wrapper={(children) => (
                                    <S.CheckboxRow>{children}</S.CheckboxRow>
                                  )}
                                >
                                  <>
                                    {/* Stop propagation so checkbox click doesn't trigger onRowClick */}
                                    {props.enableRowSelection && indx === 0 && (
                                      <div onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                          {...{
                                            checked: row.getIsSelected(),
                                            disabled: !row.getCanSelect(),
                                            onChange:
                                              row.getToggleSelectedHandler(),
                                          }}
                                        />
                                      </div>
                                    )}

                                    {props.expandColAccessor && indx === 0 && (
                                      <>
                                        {row?.getCanExpand() || expandCell ? (
                                          <RowExpandContainer>
                                            {row?.getIsExpanded() ||
                                            isExpanded ? (
                                              <FaChevronDown size={14} />
                                            ) : (
                                              <FaChevronRight size={14} />
                                            )}
                                          </RowExpandContainer>
                                        ) : null}
                                      </>
                                    )}

                                    {spreadsheetEditable ? (
                                      <DataTableSpreadsheetCell
                                        row={row.original}
                                        rowId={row.id}
                                        rowIndex={spreadsheetRowIndex}
                                        columnId={cell.column.id}
                                        editable={spreadsheetEditable}
                                      >
                                        {flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext()
                                        ) || ' - '}
                                      </DataTableSpreadsheetCell>
                                    ) : props.enableColumnResizing &&
                                      !meta?.multiLine ? (
                                      // On resizable tables, cell content
                                      // truncates to one line (…) instead of
                                      // wrapping. Pills/badges are block/flex
                                      // children, so only inline text actually
                                      // ellipsizes; their own layout is intact.
                                      // Applies to the first column too — it sits
                                      // beside the checkbox in the flex row, so
                                      // min-w-0 lets the name shrink and truncate
                                      // (covers plain-link name cells that don't
                                      // truncate on their own). Columns that
                                      // intentionally render two lines opt out via
                                      // meta.multiLine.
                                      <div className="block min-w-0 max-w-full flex-1 truncate">
                                        {flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext()
                                        ) || ' - '}
                                      </div>
                                    ) : (
                                      flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                      ) || ' - '
                                    )}
                                  </>
                                </ConditionalWrapper>
                              </S.TD>
                            )
                          })}
                        </S.TR>

                        {extraCell && (
                          <S.TRExtra>
                            <S.TDExtra
                              key={extraCell.id}
                              colSpan={props.columns.length}
                            >
                              {flexRender(
                                extraCell.column.columnDef.cell,
                                extraCell.getContext()
                              )}
                            </S.TDExtra>
                          </S.TRExtra>
                        )}

                        {expandCell && (
                          <S.TRExtra
                            key={`expanded_${row.id}`}
                            // ref={animationRef}
                            style={{
                              height: isExpanded ? 'auto' : 0,
                              ...(isLast && { border: 'none' }),
                              width: '100%',
                            }}
                          >
                            {isExpanded && (
                              <>
                                <S.TDExtra
                                  colSpan={props.extraCellColSpan ?? 4}
                                  className="sticky-table-cell"
                                >
                                  <span
                                    style={{
                                      width: 'max-content', // Hack to make the content overflow over the sticky cell
                                    }}
                                  >
                                    {flexRender(
                                      expandCell.column.columnDef.cell,
                                      expandCell.getContext()
                                    )}
                                  </span>
                                </S.TDExtra>
                                {/* -1 to account for the expand cell */}
                                <S.TDExtra
                                  colSpan={props.columns.length - 1}
                                ></S.TDExtra>
                              </>
                            )}
                          </S.TRExtra>
                        )}
                      </Fragment>
                    )
                  }
                )}

                {props.virtualizeRows && after > 0 && (
                  <S.TR
                    style={{
                      height: `${after}px`,
                    }}
                  />
                )}
              </S.TBody>
            </S.Table>
          </DataTableSpreadsheetProvider>
        </SimpleBar>
        {rowModel.rows.length === 0 && !props.loading && (
          <div
            className={cn(
              'flex w-full items-center justify-center bg-surface/75 p-6',
              props.emptyStateMinHeight
                ? props.emptyStateMinHeight
                : 'absolute inset-0'
            )}
          >
            <div className="max-w-sm rounded-xl border border-dashed border-line bg-surface px-6 py-8 text-center shadow-sm">
              <p className="text-sm font-semibold text-fg">
                {props.emptyTableText?.title || 'No results found.'}
              </p>
              <p className="mt-2 text-sm text-fg-secondary">
                {props.emptyTableText?.subtitle ||
                  'Try adjusting your filters.'}
              </p>
            </div>
          </div>
        )}
        <LoadingOverlay show={props.loading}>
          <Spinner />
        </LoadingOverlay>
      </div>

      {/* min-h reserves footer space even when pagination is disabled. Compact
          toolbars (sm/xs) drop the reserve from 72px (md) to 52px to match
          DataTablePagination's density-derived row height; tables with no
          ToolbarDensityProvider keep the legacy 72px. Treat the presence of
          paginationOptions as opt-in even if the caller didn't pass
          isPaginationEnabled — keeps every pre-existing call site (e.g.
          RebateOfferEnrollmentsTab) rendering pagination as it did before. */}
      {(props.isPaginationEnabled ||
        props.paginationOptions ||
        props.footerControls ||
        props.footerLeftControls) && (
        <div
          className={`${isCompactFooter ? 'min-h-[52px]' : 'min-h-[72px]'} flex justify-start md:justify-center items-center w-full relative`}
        >
          {props.footerLeftControls && (
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 pl-4">
              {props.footerLeftControls}
            </div>
          )}
          {(props.isPaginationEnabled || props.paginationOptions) && (
            <DataTablePagination paginationOptions={props.paginationOptions} />
          )}
          {props.footerControls && (
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
              <FooterControlsContainer>
                {props.footerControls}
              </FooterControlsContainer>
            </div>
          )}
        </div>
      )}

      {document.getElementById(getColumnSelectorRecipientId(props.tableKey)) &&
        createPortal(
          <ColumnModal
            tableKey={props.tableKey}
            sortableFields={props.sortableFields}
          />,
          document.getElementById(getColumnSelectorRecipientId(props.tableKey))!
        )}
    </div>
  )
}

const RowExpandContainer = styled.div`
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667085;
`
const FooterControlsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 10px 24px;
  gap: 8px;
`

const LoadingOverlay = styled.div<{
  show?: boolean
}>`
  display: ${({ show }) => (show ? 'flex' : 'none')};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(1px);
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  z-index: 10; /* Local loading overlay */
`
